<template>
    <div class="container-fluid">
        <!-- LOADING -->
        <Loading :style="{ display: loaded.container && loaded.drugSafe && loaded.web ? 'none' : 'block' }"></Loading>
        <!-- LOADED -->
        <div :style="{ display: loaded.container && loaded.drugSafe && loaded.web ? 'initial' : 'none' }">
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('create_user') }}</h3>
            </div>
            <div class="row mt-4">
                <div class="col-sm-8">
                    <!-- CREATE USER FORM -->
                    <ValidationObserver ref="createUserForm" v-slot="{ handleSubmit, invalid }">
                        <form role="form" @submit.prevent="handleSubmit(handleSubmitClick)">
                            <div class="row">
                                <!-- CREATE WEB USER FORM -->
                                <div class="col-sm-6" id="create_webtool_user" v-if="createWebUser">
                                    <h4 class="mb-3">{{ $t('create_web_user') }}</h4>
                                    <WebUserFormFields
                                        :edit="false"
                                        @disableDrugSafeUser="disableDrugSafeUser($event)"
                                        @loaded="setLoaded('web')"
                                        :pharmacies="pharmacies"
                                        :webUser="webUser"
                                    />
                                </div>
                                <!-- CREATE DRUGSAFE USER FORM -->
                                <div class="col-sm-6" id="create_drugsafe_user" v-if="createDrugSafeUser">
                                    <h4 class="mb-3">{{ $t('create_drugsafe_user') }}</h4>
                                    <DrugSafeUserFormFields
                                        :createWebUser="createWebUser"
                                        :edit="false"
                                        @loaded="setLoaded('drugSafe')"
                                        :drugSafeUser="drugSafeUser"
                                        :pharmacies="pharmacies"
                                    />
                                </div>
                            </div>
                            <!-- BUTTONS (SUBMIT, CANCEL) -->
                            <div class="d-flex flex-row mt-2">
                                <div class="d-flex flex-column mr-3">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        :value="$t('save')"
                                        :disabled="submitClicked || invalid"
                                    />
                                </div>
                                <div class="d-flex flex-column">
                                    <button
                                        type="button"
                                        class="btn btn-md btn-secondary"
                                        @click="returnToPreviousPage"
                                        :disabled="submitClicked"
                                    >
                                        {{ $t('cancel') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
                <!-- USER TYPE SELECTION BUTTONS -->
                <div class="col-sm-3" align="left">
                    <div class="row" v-if="!this.createWebUser || !this.createDrugSafeUser">
                        <button
                            class="btn btn-sm btn-success ml-sm-4 mb-2"
                            id="create_web_and_drugsafe_users_button"
                            v-on:click="() => create_web_and_drugsafe_users()"
                            :disabled="drugSafeUserDisabled || submitClicked"
                        >
                            {{ $t('create_web_and_drugsafe_users') }}
                        </button>
                    </div>
                    <div class="row" v-if="!(this.createWebUser && !this.createDrugSafeUser)">
                        <button
                            class="btn btn-sm btn-success ml-sm-4 mb-2"
                            id="create_only_web_user_button"
                            v-on:click="() => create_web_user_only()"
                            :disabled="submitClicked"
                        >
                            {{ $t('create_only_web_user') }}
                        </button>
                    </div>
                    <div class="row" v-if="!(!this.createWebUser && this.createDrugSafeUser)">
                        <button
                            class="btn btn-sm btn-success ml-sm-4"
                            id="create_only_dr_user_button"
                            v-on:click="() => create_drugsafe_user_only()"
                            :disabled="submitClicked"
                        >
                            {{ $t('create_only_drugsafe_user') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO ASSOCIATE SAFE USER WITH WEB USER? -->
        <b-modal id="submitConfirmationModal" centered :title="$t('set_web_user_are_you_sure_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ $t('set_web_user_are_you_sure') }}</p>
                    <p>
                        {{
                            drugSafeUser.web_user_object
                                ? `${drugSafeUser.web_user_object.first_name} ${drugSafeUser.web_user_object.last_name} (${drugSafeUser.web_user_object.email})`
                                : ''
                        }}
                    </p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="submitDrugSafeUserForm">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleConfirmationCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import { mapState } from 'vuex';
import { createDrugSafeUser, createWebUser, getPharmacies, handleAxiosError } from '@/services/axiosService';
import Loading from '@/views/partial/Loading';
import DrugSafeUserFormFields from './drugSafe/DrugSafeUserFormFields';
import WebUserFormFields from './web/WebUserFormFields';

export default {
    components: {
        DrugSafeUserFormFields,
        Loading,
        WebUserFormFields
    },
    data() {
        return {
            createDrugSafeUser: true,
            createWebUser: true,
            drugSafeUser: {
                email: null,
                first_name: null,
                language: null,
                last_name: null,
                pharmacy_id: null,
                pincode: null,
                pincode_confirmation: null,
                role: null,
                session_length: null,
                web_user: null
            },
            drugSafeUserDisabled: false,
            error: false,
            from: '',
            loaded: {
                container: false,
                drugSafe: false,
                web: false
            },
            pharmacies: [],
            submitClicked: false,
            webUser: {
                email: null,
                first_name: null,
                language: null,
                last_name: null,
                password: null,
                password_confirmation: null,
                pharmacy_id: null,
                role: 'pharmacy_owner'
            }
        };
    },
    // save previous page name for routing
    beforeRouteEnter(_to, from, next) {
        next(vm => {
            vm.from = from.name;
        });
        next();
    },
    async created() {
        if (this.user.permissions.superadmin && this.$route.params.pharmacyId) {
            // superadmin: set pharmacy_id provided in route parameter as created users' pharmacy_id
            this.drugSafeUser.pharmacy_id = this.webUser.pharmacy_id = this.$route.params.pharmacyId;
        } else {
            // otherwise set current user's pharmacy_id as created users' pharmacy_id
            this.drugSafeUser.pharmacy_id = this.webUser.pharmacy_id = this.user.pharmacy_id;
        }
        if (this.user.permissions.superadmin) {
            // superadmin: get pharmacies (without web users) for pharmacy selection
            await this.getPharmacies('false');
        }
        // set container to loaded
        this.setLoaded('container');
    },
    computed: {
        ...mapState('account', ['user'])
    },
    methods: {
        // handle "create drugsafe user only" click
        create_drugsafe_user_only() {
            this.createWebUser = false;
            this.createDrugSafeUser = true;
        },
        // handle "create web and drugsafe users" click
        create_web_and_drugsafe_users() {
            this.createWebUser = true;
            this.createDrugSafeUser = true;
        },
        // handle "create web user only" click
        create_web_user_only() {
            this.createWebUser = true;
            this.createDrugSafeUser = false;
        },
        // disallow drugsafe user creation if web user being created is superadmin
        disableDrugSafeUser(value) {
            if (value) {
                this.create_web_user_only();
            }
            this.drugSafeUserDisabled = value;
        },
        // get pharmacies
        async getPharmacies(getWebUsers) {
            try {
                this.pharmacies = await getPharmacies(getWebUsers);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // handle submit confirmation cancel
        handleConfirmationCancel() {
            this.submitClicked = false;
            this.$bvModal.hide('submitConfirmationModal');
        },
        // handle submit click
        async handleSubmitClick() {
            this.submitClicked = true;
            if (this.createWebUser) {
                // if web user created, first submit web user form
                try {
                    await this.submitWebUserForm();
                } catch (error) {
                    this.submitClicked = false;
                    return;
                }
            }
            if (this.createDrugSafeUser) {
                // if drugsafe user created, submit drugsafe user form
                if (!this.createWebUser && this.drugSafeUser.web_user) {
                    // if drugsafe user is to be associated with previously created web user, show confirmation modal
                    this.$bvModal.show('submitConfirmationModal');
                } else {
                    // otherwise submit directly
                    await this.submitDrugSafeUserForm();
                }
            }
        },
        // return to previous page (if returning to user index, retain previous tab selection)
        returnToPreviousPage() {
            if (this.from === 'user.index') {
                this.$router.push({ name: this.from, params: { tab: this.createWebUser ? 'web' : 'drugSafe' } });
            } else {
                this.$router.go(-1);
            }
        },
        // set component loaded
        setLoaded(component) {
            if (!this.error) {
                this.loaded[component] = true;
            }
        },
        // submit drugsafe user form and return to previous page
        async submitDrugSafeUserForm() {
            try {
                this.$bvModal.hide('submitConfirmationModal');
                delete this.drugSafeUser.web_user_object;
                await createDrugSafeUser(this.drugSafeUser);
                this.$toastr.s(this.$t('drugsafe_user_created'));
                this.returnToPreviousPage();
            } catch (error) {
                handleAxiosError(error);
                if (error.response.data.status === 409) {
                    // if backend returns 409 (= pin not unique), set appropriate form field error
                    this.$refs.createUserForm.setErrors({ drugSafeUserPin: ['error_pin_not_unique'] });
                }
                this.submitClicked = false;
            }
        },
        // submit web user form and return to previous page
        async submitWebUserForm() {
            try {
                const webUserId = await createWebUser(this.webUser);
                this.$toastr.s(this.$t('web_user_created'));
                if (this.createDrugSafeUser) {
                    // if drugsafe user will also be created, copy some field values to drugsafe user object
                    this.drugSafeUser.first_name = this.webUser.first_name;
                    this.drugSafeUser.last_name = this.webUser.last_name;
                    this.drugSafeUser.pharmacy_id = this.webUser.pharmacy_id;
                    this.drugSafeUser.web_user = webUserId;
                } else {
                    this.returnToPreviousPage();
                }
            } catch (error) {
                handleAxiosError(error);
                if (error.response.data.status === 409) {
                    // if backend returns 409 (= e-mail address already taken), set appropriate form field error
                    this.$refs.createUserForm.setErrors({ webUserEmail: ['error_email_taken'] });
                }
                throw new Error();
            }
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.submitClicked = false;
            }
        });
    }
};
</script>
